import { FerryIngredient } from '@apps/main/modules/recipe/ingredients';

import { toTransportVehicleType } from '@libs/search-service-smart-search';
import {
  TransportVehicleTypeQL,
  IngredientType,
  TransportVehicleType,
} from '@libs/trip-planner-interfaces';
import type {
  ExposedFerryIngredient,
  ExposedTransportIngredient,
} from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';

export function createExposedFerryIngredient(
  ferryIngredient: FerryIngredient
): ExposedFerryIngredient {
  const { fromPort, toPort, filters } = ferryIngredient;

  assertValue(
    fromPort,
    'recipeSerializer.ferry.helpers > createExposedFerryIngredient > fromPort'
  );
  assertValue(
    toPort,
    'recipeSerializer.ferry.helpers > createExposedFerryIngredient > toPort'
  );

  return {
    ingredientType: IngredientType.Ferry,
    fromIata: fromPort,
    toIata: toPort,
    departureTimeFilters: filters.departureTimeFilters,
    arrivalTimeFilters: filters.arrivalTimeFilters,
    maxDurationInHours: filters.maxDurationInHours,
    priceFilters: filters.priceFilters,
    carriers: filters.carrierFilters,
  };
}

export function createFerryIngredient(
  exposedFerryIngredient: ExposedFerryIngredient | ExposedTransportIngredient
): FerryIngredient {
  const {
    fromIata,
    toIata,
    departureTimeFilters,
    arrivalTimeFilters,
    maxDurationInHours,
    priceFilters,
    carriers,
    suggestedBy,
  } = exposedFerryIngredient;

  const ferryIngredient = new FerryIngredient();
  ferryIngredient.fromPort = fromIata;
  ferryIngredient.toPort = toIata;
  ferryIngredient.filters.departureTimeFilters = departureTimeFilters;
  ferryIngredient.filters.arrivalTimeFilters = arrivalTimeFilters;
  ferryIngredient.filters.maxDurationInHours = maxDurationInHours;
  ferryIngredient.filters.priceFilters = priceFilters;
  ferryIngredient.filters.carrierFilters = carriers ?? [];

  if ('vehicleType' in exposedFerryIngredient) {
    const vehicleType = exposedFerryIngredient.vehicleType;

    if (vehicleType) {
      ferryIngredient.vehicles = isFerryTransportVehicleTypeQl(vehicleType)
        ? [toTransportVehicleType(vehicleType)]
        : [vehicleType];
    } else {
      ferryIngredient.vehicles = [];
    }
  }

  if ('vehicles' in exposedFerryIngredient) {
    const vehicles = exposedFerryIngredient.vehicles;

    if (vehicles) {
      ferryIngredient.vehicles = vehicles.map((vehicle) =>
        isFerryTransportVehicleTypeQl(vehicle)
          ? toTransportVehicleType(vehicle)
          : vehicle
      );
    } else {
      ferryIngredient.vehicles = [TransportVehicleType.None];
    }
  }

  if (suggestedBy) {
    ferryIngredient.availabilityOrder = suggestedBy;
  }

  return ferryIngredient;
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
function isFerryTransportVehicleTypeQl(
  vehicleType: TransportVehicleType | TransportVehicleTypeQL | any
): vehicleType is TransportVehicleTypeQL {
  return Object.values(TransportVehicleTypeQL).includes(vehicleType);
}
/* eslint-enable  @typescript-eslint/no-explicit-any */
